import dynamic from "next/dynamic";
import React, { FC, PropsWithChildren, useMemo } from "react";
import { Backdrop } from "@atoms/backdrop";
import styled from "styled-components";
import { useWindowSize } from "@hooks/window-size";
import clsx from "clsx";

export type DialogProps = PropsWithChildren<{
  open: boolean;
  onClose?: () => void;
}>;

const InternalDialog: FC<DialogProps> = ({ open, children, onClose }) => {
  const { height } = useWindowSize();
  const childrenProps = useMemo(() => ({ onClose }), [onClose]);

  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, childrenProps);
    }
    return child;
  });

  const divStyles = useMemo(() => {
    if (height === undefined) {
      return undefined;
    }
    return {
      transform: !open && height ? "translateY(" + height + "px)" : undefined,
    };
  }, [open, height]);

  return (
    <>
      <Container className={clsx(open && "open")}>
        <div className="dialog" style={divStyles}>
          {childrenWithProps}
        </div>
      </Container>
      <Backdrop visible={open} onClick={onClose} />
    </>
  );
};

export const Dialog = dynamic(() => Promise.resolve(InternalDialog), {
  ssr: false,
});

const Container = styled.div`
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  overflow: auto;
  z-index: 1001;
  pointer-events: none;

  .dialog {
    background-color: #fff;
    opacity: 0;
    transition: transform 0.4s ease-in-out, opacity 0.3s linear;
    pointer-events: all;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    max-height: 100%;
    pointer-events: none;
  }

  &.open {
    .dialog {
      pointer-events: visible;
      opacity: 1;
    }
  }

  @media (min-width: 45em) {
    .dialog {
      max-width: 45em;
      width: auto;
      height: auto;
    }
  }
`;
